/**
 * PD-17299 — who holds a bank account: a business or a person.
 *
 * It decides the NACHA SEC code a debit is sent under. A business account is
 * debited as CCD (corporate credit or debit), a personal one as WEB (an entry
 * the customer authorized online). Sending the wrong one risks returns and
 * Nacha fines, and before this every processor either guessed (Adyen, NMI) or
 * labelled every account consumer (CyberSource sent WEB on everything).
 *
 * Stored on `m_payment_payment_method.accountHolderType`. Null there means
 * business: the column is new, every bank account saved before it and every
 * one the legacy application writes has none, and nearly all tenant customers
 * are businesses. The value is chosen once, when the account is saved.
 */

export const ACH_ACCOUNT_HOLDER_TYPES = ['business', 'personal'] as const;

export type AchAccountHolderType = (typeof ACH_ACCOUNT_HOLDER_TYPES)[number];

/** What a bank account with no recorded holder type is treated as. */
export const DEFAULT_ACH_ACCOUNT_HOLDER_TYPE: AchAccountHolderType = 'business';

/** The SEC codes Workd sends: CCD for a business, WEB for a person. */
export type AchSecCode = 'CCD' | 'WEB';

export function isAchAccountHolderType(
	value: unknown,
): value is AchAccountHolderType {
	return (
		typeof value === 'string' &&
		(ACH_ACCOUNT_HOLDER_TYPES as ReadonlyArray<string>).includes(value)
	);
}

/**
 * The holder type to act on: the stored or requested value when it is a
 * known one, otherwise business (see the file comment for why).
 */
export function resolveAchAccountHolderType(
	value: string | null | undefined,
): AchAccountHolderType {
	const normalized = value?.trim().toLowerCase();

	return isAchAccountHolderType(normalized)
		? normalized
		: DEFAULT_ACH_ACCOUNT_HOLDER_TYPE;
}

/** The SEC code a debit of this account is sent under. */
export function achSecCodeFor(
	accountHolderType: string | null | undefined,
): AchSecCode {
	return resolveAchAccountHolderType(accountHolderType) === 'personal'
		? 'WEB'
		: 'CCD';
}

/**
 * "Business checking", "Personal savings": the name a saved bank account is
 * shown under where its account type used to stand alone. The account type
 * comes from the method's stored subtype ('Checking' / 'Savings'); anything
 * else reads as checking, which is how every processor here treats it.
 */
export function formatBankAccountKindLabel(
	accountHolderType: string | null | undefined,
	accountSubtype: string | null | undefined,
): string {
	const holder =
		resolveAchAccountHolderType(accountHolderType) === 'personal'
			? 'Personal'
			: 'Business';
	const kind =
		accountSubtype?.trim().toLowerCase() === 'savings' ? 'savings' : 'checking';

	return `${holder} ${kind}`;
}
